import { useEffect, useMemo, useRef, useState } from "react";
import { useBackLayer } from "@hybrid-iptv/ui";
import qrcode from "qrcode-generator";
import {
  YoutubeAccountError,
  cancelConnect,
  onConnectState,
  startConnect,
  type ConnectState,
  type DeviceCodeInfo,
} from "./youtubeAccount.js";

/**
 * The Connect YouTube panel on the Social page (build guide docs/social-connect-youtube.md,
 * Phase 2b). Google's sign-in for TVs: a QR code and a short code here, sign-in on a phone.
 *
 * States, each with exactly ONE focused control so the remote never lands on nothing:
 *   idle      "Connect YouTube" button (focused on arrival)
 *   waiting   QR code, the address and the code, a countdown; "Cancel" (focused). Back = Cancel.
 *   problem   denied / expired / error: one line and "Try again" (focused)
 * Once connected the panel is simply not rendered any more (the page decides).
 */

function QrCode({ text }: { text: string }) {
  const { size, path } = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(text);
    qr.make();
    const count = qr.getModuleCount();
    let d = "";
    for (let row = 0; row < count; row++) {
      for (let col = 0; col < count; col++) {
        if (qr.isDark(row, col)) d += `M${col + 4} ${row + 4}h1v1h-1z`;
      }
    }
    return { size: count + 8, path: d };
  }, [text]);
  return (
    <svg className="connect-youtube__qr" viewBox={`0 0 ${size} ${size}`} role="img" aria-label="QR code to sign in to YouTube on a phone" shapeRendering="crispEdges">
      <rect width={size} height={size} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}

type Phase =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "waiting"; code: DeviceCodeInfo; deadline: number }
  | { kind: "problem"; message: string };

const PROBLEMS: Record<Exclude<ConnectState, "waiting" | "connected">, string> = {
  denied: "Sign-in was cancelled on the phone.",
  expired: "The code ran out before it was used.",
  error: "Couldn't finish signing in.",
};

export function ConnectYoutube({
  profileId,
  onConnected,
  focusOnArrival = true,
}: {
  profileId: string;
  onConnected?: () => void;
  /**
   * Focus the button when the panel first appears. Off where connecting is an option above other
   * content (the page's first focus belongs to its first tile); every later state still takes focus.
   */
  focusOnArrival?: boolean;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [, tick] = useState(0);
  const primary = useRef<HTMLButtonElement>(null);
  const activeProfile = useRef(profileId);
  activeProfile.current = profileId;

  // A different profile has its own state: drop whatever this one was doing.
  useEffect(() => {
    setPhase({ kind: "idle" });
    return () => void cancelConnect();
  }, [profileId]);

  useEffect(
    () =>
      onConnectState((data) => {
        if (data.profileId !== activeProfile.current) return;
        if (data.state === "connected") {
          onConnected?.();
        } else if (data.state === "denied" || data.state === "expired" || data.state === "error") {
          setPhase({ kind: "problem", message: PROBLEMS[data.state] });
        }
      }),
    [onConnected],
  );

  // The one control is focused whenever the state changes, so Back and the D-pad always have a place.
  const arrived = useRef(false);
  useEffect(() => {
    const first = !arrived.current;
    arrived.current = true;
    if (first && !focusOnArrival && phase.kind === "idle") return;
    primary.current?.focus({ preventScroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind]);

  // The countdown.
  useEffect(() => {
    if (phase.kind !== "waiting") return;
    const timer = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [phase.kind]);

  const begin = async () => {
    setPhase({ kind: "starting" });
    try {
      const code = await startConnect(profileId);
      setPhase({ kind: "waiting", code, deadline: Date.now() + code.expiresIn * 1000 });
    } catch (error) {
      const offline = error instanceof YoutubeAccountError && error.code === "offline";
      setPhase({ kind: "problem", message: offline ? "Couldn't reach Google. Check the network and try again." : PROBLEMS.error });
    }
  };

  const cancel = () => {
    void cancelConnect();
    setPhase({ kind: "idle" });
  };

  // Back is Cancel while a code is on screen: a layer on the page's Back stack, so the
  // history, the focus return and the APK's Back all behave like every other layer.
  useBackLayer(phase.kind === "waiting", cancel);

  if (phase.kind === "waiting") {
    const left = Math.max(0, Math.ceil((phase.deadline - Date.now()) / 1000));
    const minutes = Math.floor(left / 60);
    const seconds = String(left % 60).padStart(2, "0");
    return (
      <section className="connect-youtube" aria-label="Connect YouTube">
        <h2 className="connect-youtube__title">Connect YouTube</h2>
        <div className="connect-youtube__waiting">
          <QrCode text={phase.code.verificationUrl} />
          <div className="connect-youtube__steps">
            <p>
              On your phone, go to <b>google.com/device</b> and enter
            </p>
            <p className="connect-youtube__code" aria-label={`Code ${phase.code.userCode.split("").join(" ")}`}>
              {phase.code.userCode}
            </p>
            <p className="connect-youtube__hint">
              The code works for {minutes}:{seconds} more. This screen updates by itself once you have signed in.
            </p>
            <button ref={primary} type="button" className="ui-btn" onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (phase.kind === "problem") {
    return (
      <section className="connect-youtube" aria-label="Connect YouTube">
        <h2 className="connect-youtube__title">Connect YouTube</h2>
        <p className="connect-youtube__text">{phase.message}</p>
        <button ref={primary} type="button" className="ui-btn ui-btn--primary" onClick={() => void begin()}>
          Try again
        </button>
      </section>
    );
  }

  return (
    <section className="connect-youtube" aria-label="Connect YouTube">
      <h2 className="connect-youtube__title">Connect YouTube</h2>
      <p className="connect-youtube__text">
        See your own subscriptions here. SliqueTV only reads your subscriptions: it can&apos;t post, like or subscribe, and
        what you watch here isn&apos;t added to your YouTube history.
      </p>
      <button ref={primary} type="button" className="ui-btn ui-btn--primary" onClick={() => void begin()} disabled={phase.kind === "starting"}>
        {phase.kind === "starting" ? "Getting a code…" : "Connect YouTube"}
      </button>
    </section>
  );
}
